import { yupResolver } from '@hookform/resolvers/yup';
import { CANONICAL_ASSET_CLASSES, useListImageClassificationsQuery } from '@vizit/api';
import { AlertLevel, Button, ButtonLevel, ButtonSize, Text, TextSize, useToast } from '@vizit/atoms';
import { FEATURE_FLAGS } from '@vizit/constants';
import { useFeatureFlag } from '@vizit/feature-flags';
import { FormSelect } from '@vizit/forms';
import { CheckCircleIcon, ThumbsDownIcon, ThumbsUpIcon } from '@vizit/icons';
import { SelectItem } from '@vizit/molecules';
import { colors } from '@vizit/theme';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import type { ExploreImageFeedbackProps, NegativeFeedbackFormData } from './types';

// The API can still return legacy classification labels (e.g. "Pack Shot",
// "Before & After") that predate the current 14-class taxonomy — aliases for
// classes the canon list already covers under their current names. Only offer
// the canonical classes here so this picker matches the product page's
// asset-class dropdown. This is a membership filter, not a dedup: it drops
// labels outside the canon list, and relies on the API returning at most one
// entry per canonical label (two entries sharing a label would both render).
// Position in `CANONICAL_ASSET_CLASSES` doubles as the display order, so the
// canon list is the single source of both membership and ordering — this
// picker never sorts labels itself.
const CANONICAL_ASSET_CLASS_ORDER = new Map<string, number>(
  CANONICAL_ASSET_CLASSES.map((label, index) => [label, index]),
);

/**
 * ExploreImageFeedback component for collecting user feedback on image classifications
 */
function ExploreImageFeedback(props: ExploreImageFeedbackProps) {
  const {
    classificationLabel,
    productCategoryId,
    existingFeedback,
    onPositiveFeedback,
    onNegativeFeedback,
    isSubmitting = false,
  } = props;

  const { addToast } = useToast();
  const showClassification = useFeatureFlag(FEATURE_FLAGS.SHOW_IMAGE_CLASSIFICATION, false);

  const [feedbackState, setFeedbackState] = React.useState<'initial' | 'negative-form' | 'success'>(
    existingFeedback ? 'success' : 'initial',
  );

  React.useEffect(() => {
    if (existingFeedback && feedbackState === 'initial') {
      setFeedbackState('success');
    }
  }, [existingFeedback, feedbackState]);

  const { data: classifications } = useListImageClassificationsQuery(
    showClassification ? { product_category_id: productCategoryId } : undefined,
    { enabled: feedbackState === 'negative-form' },
  );

  // Drop non-canonical labels, then order by the canon list so this picker and
  // the product page's asset-class dropdown always read in the same order.
  const selectableClassifications = React.useMemo(
    () =>
      (classifications ?? [])
        .filter((classification) => CANONICAL_ASSET_CLASS_ORDER.has(classification.label))
        .sort(
          (a, b) => (CANONICAL_ASSET_CLASS_ORDER.get(a.label) ?? 0) - (CANONICAL_ASSET_CLASS_ORDER.get(b.label) ?? 0),
        ),
    [classifications],
  );

  const formSchema = yup.object().shape({
    suggested_classification_id: yup
      .string()
      .required('Please select a classification')
      .test('not-empty', 'Please select a classification', (value) => value !== ''),
  });

  const { control, handleSubmit, formState } = useForm<NegativeFeedbackFormData>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      suggested_classification_id: '',
    },
    mode: 'onChange',
  });

  const handleThumbsUp = async () => {
    const result = await onPositiveFeedback();
    if (!result.success) {
      setFeedbackState('initial');
      addToast({
        title: 'Failed to submit feedback',
        description: 'There was an error submitting your feedback. Please try again.',
        level: AlertLevel.Error,
      });
    } else {
      setFeedbackState('success');
    }
  };

  const handleThumbsDown = () => {
    setFeedbackState('negative-form');
  };

  const handleNegativeSubmit = async (data: NegativeFeedbackFormData) => {
    const result = await onNegativeFeedback(data.suggested_classification_id);
    if (!result.success) {
      setFeedbackState('negative-form');
      addToast({
        title: 'Failed to submit feedback',
        description: 'There was an error submitting your feedback. Please try again.',
        level: AlertLevel.Error,
      });
    } else {
      setFeedbackState('success');
    }
  };

  if (feedbackState === 'success') {
    return (
      <div className="flex w-full items-center h-28 gap-6 pb-12 animate-in fade-in slide-in-from-left-2 duration-500">
        <CheckCircleIcon size={24} color={colors.success['600']} />
        <Text size={TextSize.Sm}>Thanks for the feedback! We just got smarter.</Text>
      </div>
    );
  }

  if (feedbackState === 'negative-form') {
    return (
      <form
        onSubmit={handleSubmit(handleNegativeSubmit)}
        className="flex w-full h-50 items-center gap-6 animate-in slide-in-from-right duration-300"
      >
        <FormSelect
          control={control}
          name="suggested_classification_id"
          label="How would you classify this asset?"
          placeholder="Choose asset class"
          isRequired
          className="flex-1"
          isDisabled={isSubmitting}
        >
          {selectableClassifications.map((classification) => (
            <SelectItem key={classification.id} id={classification.id} textValue={classification.label}>
              {classification.label}
            </SelectItem>
          ))}
        </FormSelect>

        <Button
          type="submit"
          level={ButtonLevel.Primary}
          size={ButtonSize.Md}
          isDisabled={!formState.isValid || isSubmitting}
          isLoading={isSubmitting}
        >
          Submit
        </Button>
      </form>
    );
  }

  return (
    <div className="flex w-full items-center gap-6 pb-12">
      <Text size={TextSize.Sm}>
        Help us train our magic! Is the <span className="font-medium">{classificationLabel}</span> classification
        correct, here?
      </Text>
      <div className="flex items-center">
        <Button
          className="p-3 w-16 h-16"
          onPress={handleThumbsUp}
          level={ButtonLevel.Ghost}
          isDisabled={isSubmitting}
          iconStart={<ThumbsUpIcon size={16} color={colors['gray-utility']['400']} />}
          aria-label="Thumbs up"
        />
        <Button
          className="p-3 w-16 h-16"
          onPress={handleThumbsDown}
          level={ButtonLevel.Ghost}
          isDisabled={isSubmitting}
          iconStart={<ThumbsDownIcon size={16} color={colors['gray-utility']['400']} />}
          aria-label="Thumbs down"
        />
      </div>
    </div>
  );
}

export { ExploreImageFeedback };
export type { ExploreImageFeedbackProps };
